import React, { useState, useEffect, useRef } from "react";

interface AmendoProduct {
  varenummer: string;
  navn: string;
  priseksmva: number | string;
  prisinklmva: number | string;
}

interface ProductSearchProps {
  onProductSelect: (product: AmendoProduct | null) => void;
  products: AmendoProduct[];
}

const ProductSearch: React.FC<ProductSearchProps> = ({ 
  onProductSelect, 
  products 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<AmendoProduct[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Lukk dropdown når man klikker utenfor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchProducts = (value: string) => {
    if (!value || value.length < 1) { 
      setSuggestions([]); 
      return; 
    }
    
    const searchTermLower = value.toLowerCase();
    const filtered = products.filter(product => {
      const varenummer = product.varenummer ? product.varenummer.toLowerCase() : '';
      const navn = product.navn ? product.navn.toLowerCase() : '';
      
      return (
        varenummer === searchTermLower || 
        varenummer.startsWith(searchTermLower) || 
        varenummer.includes(searchTermLower) || 
        navn.includes(searchTermLower)
      );
    }).sort((a, b) => {
      const aVarenummer = a.varenummer ? a.varenummer.toLowerCase() : '';
      const bVarenummer = b.varenummer ? b.varenummer.toLowerCase() : '';
      
      const aExact = aVarenummer === searchTermLower;
      const bExact = bVarenummer === searchTermLower;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      const aStarts = aVarenummer.startsWith(searchTermLower);
      const bStarts = bVarenummer.startsWith(searchTermLower);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      
      return aVarenummer.localeCompare(bVarenummer);
    }).slice(0, 20);
    
    setSuggestions(filtered);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchProducts(value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleProductSelect = (product: AmendoProduct) => {
    setSearchTerm("");
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setSuggestions([]);
    if (onProductSelect) onProductSelect(product);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown': 
        e.preventDefault(); 
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : prev); 
        break;
      case 'ArrowUp': 
        e.preventDefault(); 
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1); 
        break;
      case 'Enter': 
        e.preventDefault(); 
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleProductSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape': 
        setShowSuggestions(false); 
        setSelectedIndex(-1); 
        break;
    }
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input 
        ref={inputRef} 
        type="text" 
        value={searchTerm} 
        onChange={handleInputChange} 
        onKeyDown={handleKeyDown} 
        onFocus={() => {
          if (searchTerm && suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder="Søk varenummer eller navn..." 
        className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                   focus:ring-2 focus:ring-green-500 focus:border-transparent" 
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 
                        rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((product, index) => (
            <div 
              key={product.varenummer} 
              onMouseDown={(e) => { 
                e.preventDefault(); 
                handleProductSelect(product); 
              }} 
              onMouseEnter={() => setSelectedIndex(index)} 
              className={`px-3 py-2 cursor-pointer transition-colors 
                         ${index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {product.varenummer}
                  </div>
                  <div className="text-gray-600 text-sm truncate">
                    {product.navn}
                  </div>
                </div>
                <div className="text-right ml-2">
                  <div className="text-gray-900 font-medium">
                    kr {formatPrice(product.prisinklmva)}
                  </div>
                  <div className="text-gray-500 text-xs">
                    inkl. mva
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;